import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Spinner, useEnvironment } from '@keycloak/keycloak-ui-shared';
import { createWifiGuest, getWifiGuest, updateWifiGuestPassword } from '../../api/methods';
import Grid from '@mui/material/Grid2';
import { Page } from '../../components/page/Page';
import { formatDate } from '../../utils/formatDate';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { WifiGuest } from '../../api/representations';
import { Environment } from '../../environment';
import { IconButton, Link, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Card from '../../components/card/Card';
import Button from '../../components/button/Button';
import { passwordButtonStyles, PasswordSpan } from './styles';
import CheckIcon from '../../icons/Check';
import { StyledChip } from '../../components/styles';
import { useAccountAlerts } from '../../utils/useAccountAlerts';
import { AlertVariant } from '../../alerts/Alerts';
import { useMenu } from '../../providers/MenuProvider';
import { PASSWORD_PLACEHOLDER } from '../../constants';

export const WifiPassword = () => {
  const { t } = useTranslation();
  const context = useEnvironment<Environment>();
  const { addAlert, addError } = useAccountAlerts();
  const { isOpen } = useMenu();
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isDownXl = useMediaQuery(theme.breakpoints.down('xl'));
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [showSuccessContent, setShowSuccessContent] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isLoadingAction, setLoadingAction] = useState(false);
  const [wifiGuest, setWifiGuest] = useState<WifiGuest>();

  useEffect(() => {
    if (!context.environment.features.isWifiPasswordEnabled) {
      return;
    }
    setLoading(true);
    getWifiGuest({ context })
      .then((wifiGuest) => {
        setWifiGuest(wifiGuest);
      })
      .finally(() => setLoading(false));
  }, [context]);

  if (!context.environment.features.isWifiPasswordEnabled) {
    window.location.href = context.environment.baseUrl;
    return null;
  }

  const onShowPassword = () => {
    if (!showPassword) {
      setShowPassword(true);
    } else {
      setShowPassword(false);
    }
  };

  const handleCopy = async () => {
    try {
      if (wifiGuest?.password) {
        await navigator.clipboard.writeText(wifiGuest.password);
        setShowSuccessContent(true);
        setTimeout(() => setShowSuccessContent(false), 5000);
      }
    } catch (err) {
      addError(t('failedCopy'), AlertVariant.warning);
      console.error(err);
    }
  };

  const onCreateWifiGuest = async () => {
    setLoadingAction(true);
    try {
      const result = await createWifiGuest({ context });
      setWifiGuest(result);
      addAlert(t('passwordCreated'), AlertVariant.success);
    } catch (err) {
      addError(t('passwordCreateFailed'), AlertVariant.warning);
      console.error(err);
    } finally {
      setLoadingAction(false);
    }
  };

  const onUpdateWifiPassword = async () => {
    setLoadingAction(true);
    try {
      const result = await updateWifiGuestPassword({ context });
      setWifiGuest(result);
      setIsDisabled(true);
      addAlert(t('passwordUpdated'), AlertVariant.success);
    } catch (err) {
      addError(t('passwordUpdateFailed'), AlertVariant.warning);
      console.error(err);
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <Page
      title={t('wifiPassword')}
      description={
        <>
          {t('wifiPasswordDesc')}
          <br />
          {t('wifiPasswordDescNote')}
        </>
      }>
      <Card title={t('wifiPasswordTitle')} withHeadBorder>
        {!isLoading ? (
          <Grid container aria-label="password" data-testid="password" alignItems="center">
            <Grid
              container
              spacing={{ xs: 1, lg: isOpen && isDownXl ? 1 : 15 }}
              alignItems="center"
              size={{ xs: 12, sm: isOpen && isDownLg ? 12 : 'grow' }}>
              <Grid
                container
                spacing={{ xs: 2, lg: 6 }}
                size={{ xs: 12, lg: isOpen && isDownXl ? 12 : 'auto' }}>
                <Grid>
                  <Typography sx={{ fontWeight: 600 }}>{t('password-display-name')}</Typography>
                </Grid>
                <Grid>
                  {wifiGuest?.fromDate ? (
                    <Trans i18nKey="credentialValidTo">
                      {{ date: formatDate(new Date(wifiGuest.toDate)) }}
                    </Trans>
                  ) : (
                    '-'
                  )}
                </Grid>
              </Grid>
              <Grid
                container
                display="flex"
                alignItems="center"
                spacing={0.5}
                size={{ xs: 12, lg: isOpen && isDownXl ? 12 : 'auto' }}>
                {wifiGuest?.password ? (
                  <>
                    <Tooltip placement="top" title={showSuccessContent ? t('copied') : t('copy')}>
                      <Link
                        component="button"
                        underline="none"
                        aria-label="Clipboard"
                        onClick={handleCopy}
                        color="#111">
                        {showPassword ? (
                          wifiGuest?.password
                        ) : (
                          <PasswordSpan
                            dangerouslySetInnerHTML={{ __html: PASSWORD_PLACEHOLDER }}
                          />
                        )}
                      </Link>
                    </Tooltip>
                    <IconButton disableRipple size="small" onClick={onShowPassword}>
                      {!showPassword ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                    </IconButton>
                    <StyledChip
                      icon={<CheckIcon />}
                      size="small"
                      label="Secure"
                      color="success"
                      variant="outlined"
                    />
                  </>
                ) : (
                  '-'
                )}
              </Grid>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 'auto' }}
              sx={{ alignSelf: 'center', paddingTop: { xs: 1, lg: isOpen && isDownXl ? 1 : 0 } }}>
              {wifiGuest && (
                <Button
                  variant="contained"
                  onClick={onUpdateWifiPassword}
                  data-testrole="update"
                  disabled={isDisabled}
                  size="small"
                  sx={passwordButtonStyles}
                  loading={isLoadingAction}>
                  {t('update')}
                </Button>
              )}
              {!wifiGuest && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={onCreateWifiGuest}
                  data-testrole="create"
                  sx={passwordButtonStyles}
                  loading={isLoadingAction}>
                  {t('create')}
                </Button>
              )}
            </Grid>
          </Grid>
        ) : (
          <Spinner />
        )}
      </Card>
    </Page>
  );
};

export default WifiPassword;
